import React from 'react';
import { CircularProgress, IconButton, ImageListItem, ImageListItemBar, styled } from '@mui/material';
import { Media } from '@askporter/utils';
import { RevampedIcon, RevampedIconSize } from '../../../RevampedIcon';

const StyledImageListItem = styled(ImageListItem, {
  shouldForwardProp: (prop) => prop !== 'fileState',
})<{ fileState: 'success' | 'loading' | 'error' }>(({ theme, fileState, tabIndex }) => ({
  margin: '2px',
  borderRadius: '12px',
  outline:
    fileState === 'error'
      ? `2px solid ${theme.custom?.colours?.error600}`
      : `1px solid ${theme.custom?.colours?.grey200}`,
  overflow: 'hidden',
  background: fileState === 'loading' ? 'rgba(0, 0, 0, 0.40)' : fileState === 'error' ? 'rgba(0, 0, 0, 0.50)' : 'none',
  cursor: tabIndex === 0 && fileState === 'success' ? 'pointer' : 'default',
  '&:hover':
    tabIndex === 0 && fileState === 'success'
      ? {
          outline: `2px solid ${theme.palette.primary.main}`,
        }
      : {},
  '&:focus':
    tabIndex === 0 && fileState === 'success'
      ? {
          outline: `2px solid ${theme.palette.primary.main}`,
        }
      : {},
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  margin: '8px',
  borderRadius: '8px',
  backgroundColor: '#FFF',
  '&:hover': {
    backgroundColor: '#FFF',
    filter: 'brightness(0.8)',
  },
  '&:focus': {
    backgroundColor: '#FFF',
    filter: 'brightness(0.8)',
  },
  border: `1px solid ${theme.custom?.colours?.grey300}`,
  boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
  padding: '8px',
}));

interface UploaderImageItemProps {
  /**
   * The id prefix used for testing
   */
  idPrefix: string;
  /**
   * The translation function
   */
  t: (key: string, options?: Record<string, string | number>) => string;
  /**
   * If the file remove button is disabled
   */
  removeBtnDisabled?: boolean;
  /**
   * Whether the progress indicator is showing
   */
  isShowingProgressIndicator?: boolean;
  /**
   * The file object
   */
  file: Media;
  /**
   * Whether the remove file button is visible, by default it is true
   */
  isRemoveFileBtnVisible: boolean;
  /**
   * Whether the image is clickable to open the file preview
   */
  isClickToPreviewAllowed: boolean;
}

/**
 * A component that displays an image thumbnail for successfully uploaded files.
 * It provides success, loading, error states and preview and delete actions.
 * It grows to the size of the parent container.
 */
export const UploaderImageItem = React.memo(
  ({
    idPrefix,
    t,
    file,
    removeBtnDisabled,
    isShowingProgressIndicator = true,
    isRemoveFileBtnVisible = true,
    isClickToPreviewAllowed = true,
  }: UploaderImageItemProps): React.ReactNode => {
    const translations = {
      loading: t('common:file_uploader:file_item:loading'),
      error: t('common:file_uploader:file_item:error'),
      success: t('common:file_uploader:file_item:success'),
      deleteAction: t('common:file_uploader:file_item:delete'),
      filePreviewAction: t('common:file_uploader:file_item:preview_file'),
    };

    if (!file.state) {
      return null;
    }
    return (
      <StyledImageListItem
        cols={1}
        rows={1}
        fileState={file.state}
        tabIndex={isClickToPreviewAllowed && file.state === 'success' ? 0 : -1}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            file.state === 'success' && file.setFilePreview?.();
          }
        }}
        aria-label={
          isClickToPreviewAllowed
            ? `${translations['filePreviewAction']} ${
                file.state === 'error' ? file.name : `${file.nameAndType?.name}.${file.nameAndType?.type}`
              }`
            : undefined
        }
        onClick={() => isClickToPreviewAllowed && file.state === 'success' && file.setFilePreview?.()}
        data-testid={`${idPrefix}-item`}
      >
        <img
          src={
            file.state === 'success'
              ? file.path
              : `/media/icons/design-system/file-upload/placeholder-image/placeholder.png`
          }
          alt={file.state === 'error' ? file.name : `${file.nameAndType?.name}.${file.nameAndType?.type}`}
          style={{
            filter: file.state === 'loading' ? 'blur(15px)' : file.state === 'error' ? 'blur(20px)' : 'none',
          }}
        />
        <ImageListItemBar
          position="top"
          actionIcon={
            isRemoveFileBtnVisible &&
            (file.state === 'success' || file.state === 'error') && (
              <StyledIconButton
                disabled={removeBtnDisabled}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  file?.handleRemove?.();
                }}
                aria-label={`${translations['deleteAction']} ${
                  file.state === 'error' ? file.name : `${file.nameAndType?.name}.${file.nameAndType?.type}`
                }`}
                disableFocusRipple
              >
                <RevampedIcon
                  fullPath="/media/icons/design-system/file-upload/bin-icon/24px.svg"
                  iconSize={RevampedIconSize.MD}
                />
              </StyledIconButton>
            )
          }
          actionPosition="right"
          sx={{
            backgroundColor: 'transparent',
          }}
        />
        {isShowingProgressIndicator &&
          (file.state === 'loading' ? (
            <CircularProgress
              color={'primary'}
              size={32}
              thickness={4}
              aria-label={translations[file.state]}
              sx={{
                position: 'absolute',
                right: '43%',
                top: '43%',
              }}
            />
          ) : null)}
      </StyledImageListItem>
    );
  },
);

UploaderImageItem.displayName = 'UploaderImageItem';
